import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { formatTime, tzForCountry, tzShort } from "@/lib/timezone";

export default function Attendance() {
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [centers, setCenters] = useState([]);
  const [projects, setProjects] = useState([]);
  const [f, setF] = useState({ start: "", end: "", country_id: "", center_id: "", project_id: "", status: "" });

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([_,v]) => v));
    const [r, co, c, p] = await Promise.all([
      api.get("/attendance", { params }),
      api.get("/countries"),
      api.get("/centers"),
      api.get("/projects"),
    ]);
    setRows(r.data); setCountries(co.data); setCenters(c.data); setProjects(p.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  // Timezone: use the selected country's tz; if no country, browser local (admin's own).
  const activeTz = useMemo(() => tzForCountry(countries, f.country_id), [countries, f.country_id]);
  const tzLabel = useMemo(() => tzShort(activeTz), [activeTz]);

  // Reset center when country changes and the current center isn't in that country.
  useEffect(() => {
    if (!f.country_id || !f.center_id) return;
    const c = centers.find((x) => x.id === f.center_id);
    if (c && c.country_id !== f.country_id) setF((prev) => ({ ...prev, center_id: "" }));
  }, [f.country_id, f.center_id, centers]);

  const centersForCountry = useMemo(() => {
    if (!f.country_id) return centers;
    return centers.filter((c) => c.country_id === f.country_id);
  }, [f.country_id, centers]);

  return (
    <>
      <PageHeader
        eyebrow="Records"
        title="Attendance"
        subtitle={`Every punch, timestamped and GPS-verified · times shown in ${tzLabel}${f.country_id ? " (country local)" : " (your local)"}`}
      />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div><Label className="label-mono">From</Label><Input data-testid="filter-start" type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="rounded-none" /></div>
          <div><Label className="label-mono">To</Label><Input data-testid="filter-end" type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="rounded-none" /></div>
          <div><Label className="label-mono">Country</Label>
            <Select value={f.country_id || "__all"} onValueChange={(v) => setF({ ...f, country_id: v === "__all" ? "" : v })}>
              <SelectTrigger data-testid="filter-country" className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All countries</SelectItem>
                {countries.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-mono">Center</Label>
            <Select value={f.center_id || "__all"} onValueChange={(v) => setF({ ...f, center_id: v === "__all" ? "" : v })}>
              <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All centers</SelectItem>
                {centersForCountry.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-mono">Project</Label>
            <Select value={f.project_id || "__all"} onValueChange={(v) => setF({ ...f, project_id: v === "__all" ? "" : v })}>
              <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="label-mono">Status</Label>
            <Select value={f.status || "__all"} onValueChange={(v) => setF({ ...f, status: v === "__all" ? "" : v })}>
              <SelectTrigger className="rounded-none"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="rounded-none bg-card">
                <SelectItem value="__all">All statuses</SelectItem>
                <SelectItem value="Present">Present</SelectItem>
                <SelectItem value="Late">Late</SelectItem>
                <SelectItem value="Absent">Absent</SelectItem>
                <SelectItem value="Missing_Punch_Out">Missing punch-out</SelectItem>
                <SelectItem value="Rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

          <div className="border border-border">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="label-mono">Date</TableHead>
                <TableHead className="label-mono">Employee ID</TableHead>
                <TableHead className="label-mono">Employee</TableHead>
                <TableHead className="label-mono">Center</TableHead>
                <TableHead className="label-mono">In ({tzLabel})</TableHead>
                <TableHead className="label-mono">Out ({tzLabel})</TableHead>
                <TableHead className="label-mono">Hours</TableHead>
                <TableHead className="label-mono">Status</TableHead>
                <TableHead className="label-mono">Presence</TableHead>
                <TableHead className="label-mono">GPS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} className="border-border" data-testid={`att-row-${r.id}`}>
                  <TableCell className="font-mono-brand text-xs">{r.date}</TableCell>
                  <TableCell className="font-mono-brand text-xs" data-testid={`att-empid-${r.id}`}>{r.employee_id}</TableCell>
                  <TableCell>{r.employee_name}</TableCell>
                  <TableCell>{r.center_name}</TableCell>
                  <TableCell className="font-mono-brand">{formatTime(r.punch_in_time, activeTz)}</TableCell>
                  <TableCell className="font-mono-brand">{formatTime(r.punch_out_time, activeTz)}</TableCell>
                  <TableCell className="font-mono-brand">{r.working_hours}</TableCell>
                  <TableCell><StatusPill status={r.status} /></TableCell>
                  <TableCell className="text-xs">
                    {r.heartbeats_count ? (
                      <span
                        className={`font-mono-brand ${(r.presence_breaches || 0) > 0 ? "text-orange-300" : "text-emerald-300"}`}
                        title={`${r.heartbeats_count} pings · ${r.presence_breaches || 0} breaches`}
                      >
                        {r.heartbeats_count} / {r.presence_breaches || 0}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-[10px] font-mono-brand text-muted-foreground">
                    {r.punch_in_lat?.toFixed?.(3)}, {r.punch_in_lng?.toFixed?.(3)}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow className="border-border"><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No records match your filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  );
}
